import { lazy, Suspense } from 'react';

import { FixedArea } from 'app/layout/components/Layout/FixedArea';
import {
  LayoutPageContent,
  LayoutPageSidebar,
} from 'app/layout/components/page';
import ResourceBankPage from 'app/pages/coach/ResourceBank';
import { SeoTitle } from 'app/seo/components/Title';
import { Loader } from 'shared';
import Box from 'shared_DEPRECATED/components/Box';
import Spacer from 'shared_DEPRECATED/components/Spacer';
import { routeNames } from 'shared_DEPRECATED/utils/config';
import lazyRetry from 'shared_DEPRECATED/utils/lazyRetry';

import { ChatPopup } from 'features/chat';
import { CurrentSprintProvider } from 'features/sprint/context/currentSprint';

const SharedLayoutSidebar = lazy(() =>
  lazyRetry(
    () => import('app/layout/components/Layout/AccountabilitySidebar'),
    'AccountabilitySidebar'
  )
);

const ParticipantLayoutSidebar = lazy(() =>
  lazyRetry(
    () => import('app/layout/components/Layout/ParticipantSidebar'),
    'ParticipantSidebar'
  )
);

const ParticipantSessionsPage = lazy(() =>
  lazyRetry(
    () => import('app/pages/participant/Sessions'),
    'ParticipantSessions'
  )
);

const ParticipantDashboardPage = lazy(() =>
  lazyRetry(
    () => import('app/pages/Assessments/Assessments'),
    'ParticipantDashboard'
  )
);

const Goals = lazy(() =>
  lazyRetry(() => import('app/pages/Goals/Goals'), 'Goals')
);

const GoalPage = lazy(() =>
  lazyRetry(() => import('app/pages/Goal/Goal'), 'Goal')
);

const ParticipantGroupsPage = lazy(() =>
  lazyRetry(() => import('app/pages/participant/Groups'), 'Groups')
);

const SharedDashboardPage = lazy(() =>
  lazyRetry(
    () => import('app/pages/participant/SharedDashboard'),
    'SharedDashboard'
  )
);

const SharedSprintsContent = lazy(() =>
  lazyRetry(
    () => import('features/sprint/components/Content/SharedSprintsContent'),
    'SharedSprintsContent'
  )
);

const TimelinePage = lazy(() =>
  lazyRetry(() => import('app/pages/participant/Timeline'), 'Timeline')
);

const Planning = lazy(() =>
  lazyRetry(() => import('app/pages/planning/Planning'), 'Planning')
);

const CortexChatPage = lazy(() =>
  lazyRetry(() => import('app/pages/CortexChat/CortexChat'), 'CortexChat')
);

export const participantRoutes = [
  {
    path: `/${routeNames.CHALLENGE_TRACKING}`,
    element: (
      <>
        <SeoTitle title="My Timeline" />
        <Suspense fallback={<Loader />}>
          <>
            <TimelinePage />
            <FixedArea>
              <Spacer size="zr lg md zr">
                <ChatPopup />
              </Spacer>
            </FixedArea>
          </>
        </Suspense>
      </>
    ),
  },
  {
    path: 'sessions',
    element: (
      <>
        <SeoTitle title="My Coach Sessions" />
        <Suspense fallback={<Loader />}>
          <ParticipantSessionsPage />
          <FixedArea>
            <Spacer size="zr lg md zr">
              <ChatPopup />
            </Spacer>
          </FixedArea>
        </Suspense>
      </>
    ),
  },
  {
    path: `/${routeNames.CORTEX_CHAT}`,
    element: (
      <>
        <SeoTitle title="Insights Chat" />
        <Suspense fallback={<Loader />}>
          <Box
            style={{
              display: 'flex',
              justifyContent: 'flex-start',
              height: '100%',
              width: '100%',
            }}
          >
            <LayoutPageSidebar>
              <ParticipantLayoutSidebar />
            </LayoutPageSidebar>
            <LayoutPageContent>
              <CortexChatPage />
              <FixedArea>
                <Spacer size="zr lg md zr">
                  <ChatPopup />
                </Spacer>
              </FixedArea>
            </LayoutPageContent>
          </Box>
        </Suspense>
      </>
    ),
  },
  {
    path: 'dashboard',
    element: (
      <>
        <SeoTitle title="My Dashboard" />
        <Suspense fallback={<Loader />}>
          <Box
            style={{
              display: 'flex',
              justifyContent: 'flex-start',
              height: '100%',
              width: '100%',
              maxWidth: '100vw',
            }}
          >
            <LayoutPageSidebar>
              <ParticipantLayoutSidebar />
            </LayoutPageSidebar>
            <LayoutPageContent>
              <ParticipantDashboardPage />
              <FixedArea>
                <Spacer size="zr lg md zr">
                  <ChatPopup />
                </Spacer>
              </FixedArea>
            </LayoutPageContent>
          </Box>
        </Suspense>
      </>
    ),
  },
  {
    path: routeNames.PLANNING,
    element: (
      <>
        <SeoTitle title="My Planning" />
        <Suspense fallback={<Loader />}>
          <Box
            style={{
              display: 'flex',
              justifyContent: 'flex-start',
              height: '100%',
              width: '100%',
              maxWidth: '100vw',
            }}
          >
            <LayoutPageSidebar>
              <ParticipantLayoutSidebar />
            </LayoutPageSidebar>
            <LayoutPageContent>
              <CurrentSprintProvider>
                <Planning />
              </CurrentSprintProvider>
              <FixedArea>
                <Spacer size="zr lg md zr">
                  <ChatPopup />
                </Spacer>
              </FixedArea>
            </LayoutPageContent>
          </Box>
        </Suspense>
      </>
    ),
  },
  {
    path: routeNames.GOALS,
    element: (
      <>
        <SeoTitle title="My Goals" />
        <Suspense fallback={<Loader />}>
          <Box
            style={{
              display: 'flex',
              justifyContent: 'flex-start',
              height: '100%',
              width: '100%',
            }}
          >
            <LayoutPageSidebar>
              <ParticipantLayoutSidebar />
            </LayoutPageSidebar>
            <LayoutPageContent>
              <Goals />
              <FixedArea>
                <Spacer size="zr lg md zr">
                  <ChatPopup />
                </Spacer>
              </FixedArea>
            </LayoutPageContent>
          </Box>
        </Suspense>
      </>
    ),
  },
  {
    path: `${routeNames.GOALS}/:goalId`,
    element: (
      <>
        <SeoTitle title="My Goal Details" />
        <Suspense fallback={<Loader />}>
          <GoalPage />
        </Suspense>
      </>
    ),
  },
  {
    path: routeNames.GROUPS,
    element: (
      <>
        <SeoTitle title="My Groups" />
        <Suspense fallback={<Loader />}>
          <Box
            style={{
              display: 'flex',
              justifyContent: 'flex-start',
              height: '100%',
              width: '100%',
            }}
          >
            <LayoutPageSidebar>
              <ParticipantLayoutSidebar />
            </LayoutPageSidebar>
            <LayoutPageContent>
              <ParticipantGroupsPage />
              <FixedArea>
                <Spacer size="zr lg md zr">
                  <ChatPopup />
                </Spacer>
              </FixedArea>
            </LayoutPageContent>
          </Box>
        </Suspense>
      </>
    ),
  },
  {
    path: routeNames.RESOURCE_BANK,
    element: (
      <>
        <SeoTitle title="My Groups" />
        <Suspense fallback={<Loader />}>
          <Box
            style={{
              display: 'flex',
              justifyContent: 'flex-start',
              height: '100%',
              width: '100%',
            }}
          >
            <LayoutPageSidebar>
              <ParticipantLayoutSidebar />
            </LayoutPageSidebar>
            <LayoutPageContent>
              <ResourceBankPage />
              <FixedArea>
                <Spacer size="zr lg md zr">
                  <ChatPopup />
                </Spacer>
              </FixedArea>
            </LayoutPageContent>
          </Box>
        </Suspense>
      </>
    ),
  },
  {
    path: `${routeNames.CONNECTIONS}/:email`,
    element: (
      <Suspense fallback={<Loader />}>
        <CurrentSprintProvider>
          <SharedDashboardPage />
        </CurrentSprintProvider>
      </Suspense>
    ),
    children: [
      {
        path: 'sprints/:sprintId',
        element: (
          <>
            <SeoTitle title="My Group Mate Sprint" />
            <Suspense fallback={<Loader />}>
              <LayoutPageSidebar>
                <SharedLayoutSidebar />
              </LayoutPageSidebar>
              <LayoutPageContent>
                <SharedSprintsContent />
                <FixedArea>
                  <Spacer size="zr lg md zr">
                    <ChatPopup />
                  </Spacer>
                </FixedArea>
              </LayoutPageContent>
            </Suspense>
          </>
        ),
      },
    ],
  },
];
